import { useState, useMemo, useEffect } from 'react';
import { Box, Paper, Typography, Stack, FormControl, InputLabel, Select as MuiSelect, MenuItem, Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import DataTable from '../../components/DataTable';
import { useCompanyDataList } from '../../stores/companyStore';
import { useMasterVehicleList } from '../../stores/masterVehicleStore';
import PickerField from '../../components/PickerField';
import usePickerPagination from '../../hooks/usePickerPagination';
import { useNotifier } from '../../components/Notifier';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorageIcon from '@mui/icons-material/Storage';
import { useLanguage } from '../../utils/i18n';

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return 'Rp HAHAHA';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0
  }).format(value);
};

export default function BatteryManagement() {
  const { t } = useLanguage();
  const notify = useNotifier();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    try {
      const stored = window.localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return [String(stored)];
      }
    } catch {}
    return [];
  });

  const batteryColumns = useMemo(
    () => [
      { field: 'licensePlate', headerName: t('reports.licensePlate', 'License Plate'), width: 130 },
      { field: 'vehicleType', headerName: t('general.vehicleType', 'Vehicle Type'), width: 150 },
      { field: 'distance', headerName: t('reportModule.totalDistanceKm', 'Total Distance (KM)'), width: 120 },
      { field: 'energy', headerName: t('reportModule.energyUsedKwh', 'Energy Used (Kwh)'), width: 150 },
      { field: 'kwhKm', headerName: t('reportModule.kwhKm', 'Kwh/Km'), width: 120 },
      { field: 'costPerKwh', headerName: t('reportModule.costPerKwh', 'Cost per Kwh'), flex: 1, valueGetter: (row) => formatCurrency(row) },
      { field: 'totalCost', headerName: t('tireRepairScrap.totalCost', 'Total Cost'), flex: 1, valueGetter: (row) => formatCurrency(row) }
    ],
    [t]
  );

  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);

  const [periodType, setPeriodType] = useState('last_week');
  const [fromDate, setFromDate] = useState(dayjs().subtract(7, 'day').startOf('day').second(0));
  const [toDate, setToDate] = useState(dayjs().endOf('day').second(59));
  const [dateError, setDateError] = useState('');

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const selectedCompaniesParam = useMemo(() => {
    if (selectedCompanyIds.length === 0 || selectedCompanyIds.includes('all')) {
      return companyOptions.map((c) => ({ value: c.value }));
    }
    return selectedCompanyIds.map((id) => ({ value: id }));
  }, [selectedCompanyIds, companyOptions]);

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, state.companyId, state.searchKey, state.searchValue],
    formatOption: (p) => ({
      value: p.id,
      label: p.license_plate,
      original: p
    }),
    searchKey: 'license_plate',
    companyId: selectedCompaniesParam
  });
  const vehicleOptions = vehiclePickerParams.options;

  const handleVehicleChange = (newSelectedIds) => {
    setSelectedVehicleIds(newSelectedIds);
    const newSelectedObjs = newSelectedIds.map((id) => {
      const existingObj = selectedVehicles.find((obj) => obj.value === id);
      if (existingObj) return existingObj;
      const optionMatch = vehicleOptions.find((opt) => opt.value === id);
      return optionMatch || { value: id, label: String(id) };
    });
    setSelectedVehicles(newSelectedObjs);
  };

  const validateDateRange = (start, end) => {
    if (!start || !end) {
      setDateError(t('mapsTracking.dateCannotBeEmpty', 'Date cannot be empty'));
      return false;
    }
    if (end.isBefore(start)) {
      setDateError(t('reportModule.dateInvalidRange', 'End time cannot be earlier than start time'));
      return false;
    }
    const diff = end.diff(start, 'day');
    if (diff > 30) {
      setDateError(t('reportModule.dateMaxRange', 'Time range maximum is 30 days'));
      return false;
    }
    setDateError('');
    return true;
  };

  const handlePeriodChange = (e) => {
    const val = e.target.value;
    setPeriodType(val);

    const now = dayjs();
    if (val === 'last_24h') {
      setFromDate(now.subtract(24, 'hour').second(0));
      setToDate(now.second(59));
      setDateError('');
    } else if (val === 'last_week') {
      setFromDate(now.subtract(7, 'day').startOf('day').second(0));
      setToDate(now.endOf('day').second(59));
      setDateError('');
    } else if (val === 'last_month') {
      setFromDate(now.subtract(1, 'month').startOf('day').second(0));
      setToDate(now.endOf('day').second(59));
      setDateError('');
    } else {
      setDateError('');
    }
  };

  const handleSearch = () => {
    if (!validateDateRange(fromDate, toDate)) return;
    setLoading(true);

    setTimeout(() => {
      const newRows = [];
      const vIds = selectedVehicleIds.length > 0 ? selectedVehicleIds : [t('reportModule.all', 'ALL')];

      vIds.forEach((vId, idx) => {
        for (let i = 0; i < 2; i++) {
          const licensePlate = vId === 'ALL' ? `B ${1000 + idx * 10 + i} KEL` : selectedVehicles.find((v) => v.value === vId)?.label || vId;
          newRows.push({
            id: `${vId}-${i}-${Date.now()}`,
            licensePlate,
            vehicleType: 'EV Dump Truck',
            distance: Math.floor(Math.random() * 500) + 100,
            energy: Math.floor(Math.random() * 300) + 50,
            kwhKm: (Math.random() * 2 + 0.5).toFixed(2),
            costPerKwh: 1500,
            totalCost: Math.floor(Math.random() * 500000) + 100000
          });
        }
      });

      setRows(newRows);
      setLoading(false);
    }, 1000);
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportToExcel = async () => {
    if (rows.length === 0) {
      notify({ message: t('reports.noDataExport', 'No data to export'), severity: 'warning' });
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('reports.batteryManagement', 'Battery Management'));

    worksheet.columns = batteryColumns.map((c) => ({ header: c.headerName, key: c.field, width: 20 }));

    rows.forEach((row) => worksheet.addRow(row));
    worksheet.getRow(1).font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `battery_management_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h4" fontWeight={700} display="flex" alignItems="center" gap={1}>
          <StorageIcon fontSize="large" color="primary" />
          {t('reports.batteryManagement', 'Battery Management (Dummy)')}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 0.5,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(to right bottom, #ffffff, #fcfcfc)'
        }}
      >
        <Box
          sx={{
            p: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            bgcolor: 'rgba(25, 118, 210, 0.04)',
            borderRadius: 2
          }}
        >
          {/* <InfoOutlinedIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} /> */}
          <Typography variant="body2" color="#1976d2" fontWeight="500" sx={{ lineHeight: 1.5 }}>
            {t('reports.batteryManagementDesc', 'Detailed EV battery activity including energy usage (kWh).')}
          </Typography>
          {(localStorage.getItem('rolename') === 'Administrator' ||
            (() => {
              try {
                return JSON.stringify(JSON.parse(localStorage.getItem('master_menu') || '[]')).includes('"modules_path":"report-catalog"');
              } catch {
                return false;
              }
            })()) && (
            <Box>
              <Button
                startIcon={<ArrowBackIcon fontSize="small" />}
                onClick={() => navigate('/report-catalog?name=report-catalog')}
                size="small"
                variant="contained"
                sx={{
                  bgcolor: 'primary',
                  color: '#ffffff',
                  textTransform: 'none',
                  px: 0.5,
                  py: 0,
                  minWidth: 0,
                  '&:hover': { bgcolor: '#36477c', color: '#ffffff' }
                }}
              >
                {t('reports.reportCatalog', 'Report Catalog')}
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <PickerField
                label={t('general.selectCompany', 'Select Company')}
                value={selectedCompanyIds}
                selectedItems={companyOptions.filter((c) => selectedCompanyIds.includes(c.value))}
                {...companyPickerParams}
                options={companyOptions}
                onChange={(ids) => {
                  setSelectedCompanyIds(ids || []);
                  setSelectedVehicleIds([]);
                  setSelectedVehicles([]);
                }}
                dialogTitle={t('general.selectCompany', 'Select Company')}
                multiSelect={true}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <PickerField
                label={t('reports.selectVehicle', 'Select Vehicle')}
                value={selectedVehicleIds}
                selectedItems={selectedVehicles}
                {...vehiclePickerParams}
                options={vehicleOptions}
                onChange={handleVehicleChange}
                dialogTitle={t('reports.selectVehicle', 'Select Vehicle')}
                multiSelect={true}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('revenue.period', 'Period')}</InputLabel>
              <MuiSelect value={periodType} onChange={handlePeriodChange} label={t('revenue.period', 'Period')}>
                <MenuItem value="last_24h">{t('reports.last24Hours', 'Last 24 Hours')}</MenuItem>
                <MenuItem value="last_week">{t('reports.lastWeek', 'Last Week')}</MenuItem>
                <MenuItem value="last_month">{t('time.lastMonth', 'Last Month')}</MenuItem>
                <MenuItem value="custom">{t('reports.custom', 'Custom')}</MenuItem>
              </MuiSelect>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label={t('reports.from', 'From')}
                format="DD/MM/YYYY HH:mm:ss"
                value={fromDate}
                onChange={(val) => {
                  const newV = val ? val.second(0) : val;
                  setFromDate(newV);
                  setPeriodType('custom');
                  validateDateRange(newV, toDate);
                }}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
              <DateTimePicker
                label={t('reports.to', 'To')}
                format="DD/MM/YYYY HH:mm:ss"
                value={toDate}
                onChange={(val) => {
                  const newV = val ? val.second(59) : val;
                  setToDate(newV);
                  setPeriodType('custom');
                  validateDateRange(fromDate, newV);
                }}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
            </LocalizationProvider>

            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading || !!dateError}
              sx={{ px: 3, py: 1, minWidth: 120 }}
            >
              {t('general.search', 'Search')}
            </Button>
          </Stack>
        </Box>

        {dateError && (
          <Typography color="error" variant="caption" sx={{ display: 'block', mb: 2 }}>
            {dateError}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="outlined" onClick={exportToExcel} disabled={rows.length === 0}>
            {t('revenue.exportExcel', 'Export Excel')}
          </Button>
        </Box>

        <Box>
          <DataTable
            rows={rows}
            columns={batteryColumns}
            loading={loading}
            disableRowSelectionOnClick
            hideAddButton={true}
            hideExportButton={true}
            hideSearchField={true}
          />
        </Box>
      </Paper>
    </Box>
  );
}
